/**
 * Recording a death, and correcting one — DECISIONS.md #53.
 *
 * **Two times, both defaulting to now and both editable back** — the time of death and when the death
 * was established. Typed as `YYYY-MM-DD HH:mm` and read by the same strict parser as a transfer's
 * time, never by the browser's date control (I18N.md §3). A death found at the morning round and
 * documented an hour later is the normal case (AGENTS.md §4).
 *
 * **The police question has no preselected answer.** It is the first thing an investigation looks
 * for, and a default would turn "nobody answered" into an answer.
 *
 * **Withdrawal is a correction**, offered only when correcting: a death on the wrong patient becomes
 * `entered-in-error`, and the row stays in the record.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { formatClinicalDateTime, parseClinicalDateTime } from '../format/clinicalDate'
import {
  POLICE_NOTIFICATIONS,
  writeDeath,
  DeathRefusedError,
  DeathUnavailableError,
  type DeathRefusalCode,
  type DeathRefusalParams,
  type PoliceNotification,
} from './deathApi'
import { deathRefusalMessage, deathUnavailableMessage, policeLabel } from './deathDisplay'
import type { PatientDeathView } from './patientOverviewApi'

/** Longest accepted note. Mirrors `DeathContent`'s limit on the server. */
const MAXIMUM_NOTE_LENGTH = 2000

interface DeathDraft {
  /** `YYYY-MM-DD HH:mm` as typed, local time. */
  readonly diedAt: string
  readonly estimated: boolean
  readonly pronouncedAt: string
  readonly police: PoliceNotification | null
  readonly note: string
  readonly withdraw: boolean
}

type Phase = { readonly kind: 'editing' } | { readonly kind: 'saving' } | { readonly kind: 'saved' }

type Notice =
  | {
      readonly kind: 'refused'
      readonly code: DeathRefusalCode | 'UNKNOWN'
      readonly params: DeathRefusalParams
    }
  | { readonly kind: 'unavailable' }

/**
 * The typed time as the UTC instant the server parses — or exactly as typed when it does not name one
 * local minute, for the server to refuse by name. Never repaired, never dropped.
 */
function toInstant(typed: string): string {
  const parsed = parseClinicalDateTime(typed.trim())
  return parsed === null ? typed : parsed.toISOString()
}

/** A stored instant as the box shows it, for prefilling a correction. */
function typed(instant: string): string {
  try {
    return formatClinicalDateTime(instant)
  } catch {
    return instant
  }
}

/**
 * The death form.
 *
 * @param patientId Whose death this is.
 * @param death The current record when correcting, or null to record one. Read on every render, so a
 *   refetch after a stale refusal moves the version this form supersedes along with it.
 * @param onSaved Written; the overview refetches.
 * @param onStale Somebody else changed it, or one already exists. Refetches and keeps the draft.
 * @param onClose Leaves the form.
 */
export function DeathForm({
  patientId,
  death,
  onSaved,
  onStale,
  onClose,
}: {
  readonly patientId: string
  readonly death: PatientDeathView | null
  readonly onSaved: () => void
  readonly onStale: () => void
  readonly onClose: () => void
}) {
  const { t } = useTranslation(['common', 'patient', 'death'])

  // Fixed when the form opens. The save refreshes the overview, which hands this form the death it
  // has just recorded — a mode read from props on every render would then call a first recording a
  // correction in its own confirmation, which is what it did until the maintainer's first browser
  // pass (2026-10-09). And a recording refused because a death already exists stays a recording: the
  // physician is told to correct that one, not switched into correcting it with this draft.
  const [correcting] = useState(death !== null)

  // The death this correction was opened on. The live prop moves on after a stale refusal, which is
  // what keeps `supersedes` current — but it can also become null, when somebody else withdraws the
  // death meanwhile, and a correction must then still be sent as a correction of *that* death so the
  // server can refuse it (DEATH_RETRACTED). Falling through to the collection would record a new,
  // signed death on a patient another physician has just said was wrong (review of 2026-10-09).
  const [openedOn] = useState(death)

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' })
  const [notice, setNotice] = useState<Notice | null>(null)
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)

  // Fixed at mount, so the boxes do not change under a physician who is reading them.
  const [initial] = useState<DeathDraft>(() => {
    const now = formatClinicalDateTime(new Date())

    return death === null
      ? { diedAt: now, estimated: false, pronouncedAt: now, police: null, note: '', withdraw: false }
      : {
          diedAt: typed(death.diedAt),
          estimated: death.timeOfDeathEstimated,
          pronouncedAt: typed(death.pronouncedAt),
          police: (POLICE_NOTIFICATIONS as readonly string[]).includes(death.policeNotification)
            ? (death.policeNotification as PoliceNotification)
            : null,
          note: death.note ?? '',
          withdraw: false,
        }
  })

  const [draft, setDraft] = useState<DeathDraft>(initial)

  /** See `MedicationCorrectionForm`: a submit in flight is not aborted on unmount. */
  const live = useRef(true)
  useEffect(() => {
    live.current = true
    return () => {
      live.current = false
    }
  }, [])

  const update = <Field extends keyof DeathDraft>(field: Field, value: DeathDraft[Field]) => {
    setDraft((previous) => ({ ...previous, [field]: value }))
    setNotice(null)
  }

  const dirty = (Object.keys(initial) as (keyof DeathDraft)[]).some(
    (field) => draft[field] !== initial[field],
  )
  const busy = phase.kind === 'saving'

  useEffect(() => {
    if (!dirty || phase.kind === 'saved') {
      return
    }

    const warn = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = 'unsaved'
    }

    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty, phase.kind])

  const submit = () => {
    if (busy) {
      return
    }

    setPhase({ kind: 'saving' })
    setNotice(null)

    const note = draft.note.trim()

    // The death this correction was opened on, always. Its *version* follows the live prop, so a stale
    // refusal and a refetch move `supersedes` on — but only while the live prop is still that death.
    // If somebody withdrew it and recorded another, the overview now shows a different death, and
    // taking its id would land this draft on a record the physician never opened (review of
    // 2026-10-12); naming the original instead lets the server answer DEATH_RETRACTED.
    const target =
      correcting && openedOn !== null
        ? {
            deathId: openedOn.deathId,
            versionId: (death?.deathId === openedOn.deathId ? death : openedOn).versionId,
          }
        : null

    writeDeath(
      patientId,
      target?.deathId ?? null,
      {
        // Read here and not at mount: after a stale refusal and a refetch it is a different version.
        ...(target !== null ? { supersedes: target.versionId } : {}),
        ...(draft.withdraw ? { status: 'entered-in-error' as const } : {}),
        diedAt: toInstant(draft.diedAt),
        timeOfDeathEstimated: draft.estimated,
        pronouncedAt: toInstant(draft.pronouncedAt),
        policeNotification: draft.police,
        note: note === '' ? null : note,
      },
      new AbortController().signal,
    ).then(
      () => {
        if (!live.current) {
          return
        }
        setPhase({ kind: 'saved' })
        onSaved()
      },
      (cause: unknown) => {
        if (!live.current) {
          return
        }

        setPhase({ kind: 'editing' })

        if (cause instanceof DeathRefusedError) {
          setNotice({ kind: 'refused', code: cause.code, params: cause.params })

          // Either way the screen is behind the record, and the overview has to catch up.
          if (cause.code === 'DEATH_VERSION_STALE' || cause.code === 'DEATH_ALREADY_RECORDED') {
            onStale()
          }
        } else if (cause instanceof DeathUnavailableError) {
          setNotice({ kind: 'unavailable' })
        } else {
          setNotice({ kind: 'refused', code: 'UNKNOWN', params: {} })
        }
      },
    )
  }

  const close = () => {
    if (dirty && phase.kind === 'editing') {
      setConfirmingDiscard(true)
      return
    }
    onClose()
  }

  const heading = correcting ? t('death:screen.correctionHeading') : t('death:screen.heading')

  if (phase.kind === 'saved') {
    return (
      <section className="registration patient-correction" aria-labelledby="death-form-heading">
        <h3 id="death-form-heading">{heading}</h3>

        <p className="registration__registered" role="status">
          {correcting ? t('death:screen.savedCorrection') : t('death:screen.saved')}
        </p>

        <button type="button" onClick={onClose}>
          {t('death:screen.close')}
        </button>
      </section>
    )
  }

  return (
    <section className="registration patient-correction" aria-labelledby="death-form-heading">
      <header className="registration__header">
        <h3 id="death-form-heading">{heading}</h3>

        <p className="registration__explains">
          {correcting ? t('death:screen.correctionExplanation') : t('death:screen.explanation')}
        </p>
      </header>

      <p className="registration__hint">{t('death:screen.requiredHint')}</p>

      <div className="registration__field registration__field--datetime">
        <label htmlFor="death-field-died-at">{t('death:field.diedAt')}</label>

        <p id="death-field-died-at-hint" className="registration__hint">
          {t('death:fieldHint.diedAt')}
        </p>

        <input
          id="death-field-died-at"
          // Text, not the browser's date control — `MedicationCorrectionForm`'s end date says why.
          type="text"
          value={draft.diedAt}
          disabled={busy}
          autoFocus
          aria-describedby="death-field-died-at-hint"
          onChange={(event) => update('diedAt', event.target.value)}
        />
      </div>

      <div className="registration__field registration__field--checkbox">
        <label htmlFor="death-field-estimated">
          <input
            id="death-field-estimated"
            type="checkbox"
            checked={draft.estimated}
            disabled={busy}
            onChange={(event) => update('estimated', event.target.checked)}
          />{' '}
          {t('death:field.estimated')}
        </label>
      </div>

      <div className="registration__field registration__field--datetime">
        <label htmlFor="death-field-pronounced-at">{t('death:field.pronouncedAt')}</label>

        <p id="death-field-pronounced-at-hint" className="registration__hint">
          {t('death:fieldHint.pronouncedAt')}
        </p>

        <input
          id="death-field-pronounced-at"
          type="text"
          value={draft.pronouncedAt}
          disabled={busy}
          aria-describedby="death-field-pronounced-at-hint"
          onChange={(event) => update('pronouncedAt', event.target.value)}
        />
      </div>

      <fieldset className="registration__field registration__choices" aria-describedby="death-field-police-hint">
        <legend>{t('death:field.police')}</legend>

        <p id="death-field-police-hint" className="registration__hint">
          {t('death:fieldHint.police')}
        </p>

        {POLICE_NOTIFICATIONS.map((answer) => (
          <label key={answer} className="registration__choice">
            <input
              type="radio"
              name="death-field-police"
              value={answer}
              checked={draft.police === answer}
              disabled={busy}
              onChange={() => update('police', answer)}
            />{' '}
            {policeLabel(answer, t)}
          </label>
        ))}
      </fieldset>

      <div className="registration__field">
        <label htmlFor="death-field-note">
          {t('death:field.note')}
          <span className="registration__optional"> ({t('death:screen.optional')})</span>
        </label>

        <p id="death-field-note-hint" className="registration__hint">
          {t('death:fieldHint.note')}
        </p>

        <textarea
          id="death-field-note"
          value={draft.note}
          disabled={busy}
          rows={3}
          aria-describedby="death-field-note-hint"
          maxLength={MAXIMUM_NOTE_LENGTH}
          onChange={(event) => update('note', event.target.value)}
        />
      </div>

      {correcting && (
        <div className="registration__field registration__field--checkbox">
          <label htmlFor="death-field-withdraw">
            <input
              id="death-field-withdraw"
              type="checkbox"
              checked={draft.withdraw}
              disabled={busy}
              onChange={(event) => update('withdraw', event.target.checked)}
            />{' '}
            {t('death:field.withdraw')}
          </label>
        </div>
      )}

      <div aria-live="assertive">
        {notice !== null && (
          <div className="registration__notice" role="alert">
            <p>
              {notice.kind === 'unavailable'
                ? deathUnavailableMessage(t)
                : deathRefusalMessage(notice.code, notice.params, t)}
            </p>
          </div>
        )}
      </div>

      <footer className="registration__actions">
        <p className="registration__final">{t('death:screen.final')}</p>

        <button type="button" className="registration__register" disabled={busy} onClick={submit}>
          {busy
            ? t('death:screen.saving')
            : correcting
              ? t('death:screen.saveCorrection')
              : t('death:screen.save')}
        </button>

        <button type="button" disabled={busy} onClick={close}>
          {t('death:screen.cancel')}
        </button>
      </footer>

      {confirmingDiscard && (
        <div
          className="registration__discard"
          role="alertdialog"
          aria-label={t('death:screen.discardQuestion')}
        >
          <p>{t('death:screen.discardQuestion')}</p>

          <button type="button" onClick={() => setConfirmingDiscard(false)}>
            {t('death:screen.discardCancel')}
          </button>
          <button type="button" onClick={onClose}>
            {t('death:screen.discardConfirm')}
          </button>
        </div>
      )}
    </section>
  )
}
